import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Seahorse — a curled golden seahorse standing upright on its own coiled tail:
// a chunky S-curve of a body with a cream tummy, a tube snout poking forward,
// a spiky little coronet crown, big close-set eyes and a fan of a back fin
// that flutters like a tiny motor. Mess batch: chunky toy proportions, warm
// saturated colours.
//
// Frame: faces +Z (the snout and eyes), stands on the ground at y = 0 (the
// coiled tail is the base it balances on). ~0.5 m tall.
//
// Rig (flat, each named node about its own pivot):
//   - "body"  : everything above the tail coil — trunk, head, snout, crown.
//               Pivot at the top of the coil, so it bobs and sways like a
//               spring rider. The coil stays planted.
//   - "fin"   : the back fin fan. Pivot at its root on the spine, flutters.

const GOLD = "#f0b23e";        // main hide — warm seahorse gold
const GOLD_DARK = "#c98d20";   // shading (back ridge, coil underside, crown)
const CREAM = "#f6efe3";       // tummy + eye whites
const EYE_DARK = "#3b3230";    // pupils (warm dark, not black)
const FIN = "#e8734a";         // the back fin — a coral pop against the gold
const CHEEK = "#e8a08a";       // rosy cheek dots

// Where the body meets the coil — the spring-rider pivot.
const COIL_TOP: [number, number, number] = [0, 0.14, -0.04];
// Where the back fin roots on the spine.
const FIN_ROOT: [number, number, number] = [0, 0.28, -0.115];

export const seahorse = defineModel({
  id: "seahorse",
  batchId: "mess",
  name: "Seahorse",
  tags: ["creature", "mess", "cute", "animated", "sea", "ocean", "seahorse", "animal"],
  summary: "A curled golden seahorse on its coiled tail, back fin fluttering.",
  description:
    "A storybook seahorse: a chunky golden S-curve standing upright on its own " +
    "coiled tail, with a cream tummy, a tube snout, a spiky coronet crown, big " +
    "close-set eyes and a coral back-fin fan. The idle bobs and sways the body " +
    "on the coil like a spring rider; fin-flutter spins up the back fin with a " +
    "happy full-body wiggle.",
  author: "models",
  footprint: 0.3,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── the coiled tail (the planted base — static, unnamed) ─────────────
      // The coil: a chunky torus standing in the side plane (normal along X),
      // curling forward under the body like a rolled-up party horn.
      { geometry: new THREE.TorusGeometry(0.062, 0.038, 10, 20), color: GOLD,
        position: [0, 0.068, 0.015], rotation: [0, Math.PI / 2, 0] },
      // The curl's tip: a small ball tucked into the middle of the coil.
      { geometry: new THREE.SphereGeometry(0.032, 8, 6), color: GOLD_DARK,
        position: [0, 0.07, 0.02] },
      // The tail stock thickening up out of the coil toward the trunk.
      { geometry: new THREE.SphereGeometry(0.055, 10, 8), color: GOLD,
        position: [0, 0.135, -0.035], scale: [0.9, 1.3, 1.0] },

      // ── the trunk (named "body" from here up) ────────────────────────────
      // The chunky belly, leaning back a touch so the pose reads as a proud S.
      { geometry: new THREE.SphereGeometry(0.105, 14, 12), color: GOLD,
        position: [0, 0.26, -0.02], scale: [0.85, 1.25, 1.0],
        rotation: [-0.15, 0, 0], name: "body" },
      // Cream tummy plate on the front of the trunk.
      { geometry: new THREE.SphereGeometry(0.09, 12, 10), color: CREAM,
        position: [0, 0.25, 0.025], scale: [0.72, 1.1, 0.8],
        rotation: [-0.15, 0, 0], name: "body" },
      // Three little back-ridge bumps down the spine.
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: GOLD_DARK,
        position: [0, 0.35, -0.095], name: "body" },
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: GOLD_DARK,
        position: [0, 0.28, -0.11], name: "body" },
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: GOLD_DARK,
        position: [0, 0.21, -0.1], name: "body" },

      // ── the head ─────────────────────────────────────────────────────────
      // A round head pitched forward at the top of the S.
      { geometry: new THREE.SphereGeometry(0.085, 14, 12), color: GOLD,
        position: [0, 0.42, 0.03], scale: [0.95, 1.0, 1.05], name: "body" },
      // The tube snout: a little trumpet poking forward and slightly down,
      // clearly below the eyes.
      { geometry: new THREE.CylinderGeometry(0.028, 0.036, 0.11, 10), color: GOLD,
        position: [0, 0.385, 0.12], rotation: [Math.PI / 2 - 0.3, 0, 0], name: "body" },
      // A cream ring at the snout's mouth, like a tiny bugle bell.
      { geometry: new THREE.CylinderGeometry(0.032, 0.032, 0.02, 10), color: CREAM,
        position: [0, 0.369, 0.169], rotation: [Math.PI / 2 - 0.3, 0, 0], name: "body" },
      // The coronet: a wee dark-gold crown of two spikes on the crown.
      { geometry: new THREE.ConeGeometry(0.022, 0.06, 8), color: GOLD_DARK,
        position: [0, 0.51, 0.01], rotation: [-0.2, 0, 0], name: "body" },
      { geometry: new THREE.ConeGeometry(0.018, 0.045, 8), color: GOLD_DARK,
        position: [0, 0.495, -0.04], rotation: [-0.5, 0, 0], name: "body" },

      // ── the face (big and close together) ───────────────────────────────
      // Eye whites, set into the front of the head so they never poke past
      // its silhouette from behind.
      { geometry: new THREE.SphereGeometry(0.034, 12, 10), color: CREAM,
        position: [-0.034, 0.432, 0.092], name: "body" },
      { geometry: new THREE.SphereGeometry(0.034, 12, 10), color: CREAM,
        position: [0.034, 0.432, 0.092], name: "body" },
      // Dark pupils, set slightly forward.
      { geometry: new THREE.SphereGeometry(0.018, 10, 8), color: EYE_DARK,
        position: [-0.032, 0.432, 0.119], name: "body" },
      { geometry: new THREE.SphereGeometry(0.018, 10, 8), color: EYE_DARK,
        position: [0.032, 0.432, 0.119], name: "body" },
      // Tiny cream highlights for sparkle.
      { geometry: new THREE.SphereGeometry(0.007, 8, 6), color: CREAM,
        position: [-0.025, 0.44, 0.133], name: "body" },
      { geometry: new THREE.SphereGeometry(0.007, 8, 6), color: CREAM,
        position: [0.039, 0.44, 0.133], name: "body" },
      // Rosy cheeks below the eyes.
      { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: CHEEK,
        position: [-0.062, 0.41, 0.07], scale: [1, 0.7, 0.6], name: "body" },
      { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: CHEEK,
        position: [0.062, 0.41, 0.07], scale: [1, 0.7, 0.6], name: "body" },

      // ── the back fin (named, the flutter motor) ──────────────────────────
      // A flattened coral fan on the spine between the shoulder blades.
      { geometry: new THREE.SphereGeometry(0.06, 10, 8), color: FIN,
        position: [0, 0.29, -0.15], scale: [0.35, 1.25, 0.9],
        rotation: [0.25, 0, 0], name: "fin" },
      // A smaller deeper lobe behind it for a two-layer fan read.
      { geometry: new THREE.SphereGeometry(0.045, 8, 6), color: GOLD_DARK,
        position: [0, 0.285, -0.175], scale: [0.25, 1.1, 0.8],
        rotation: [0.25, 0, 0], name: "fin" },
    ];
  },
  animations: [
    // 1. Idle — the body bobs and sways gently on the coil like a spring
    //    rider, the fin waving along softly. The coil never moves.
    {
      id: "idle",
      label: "Idle",
      duration: 3.2,
      loop: true,
      tracks: [
        {
          target: "body", pivot: COIL_TOP,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0.018, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.04, 0, 0.03], ease: "sine" },
            { t: 0.8, value: [-0.03, 0, 0], ease: "sine" },
            { t: 1.6, value: [0.04, 0, -0.03], ease: "sine" },
            { t: 2.4, value: [-0.03, 0, 0], ease: "sine" },
            { t: 3.2, value: [0.04, 0, 0.03], ease: "sine" },
          ],
        },
        {
          target: "fin", pivot: FIN_ROOT,
          rotation: [
            { t: 0, value: [0, -0.15, 0], ease: "sine" },
            { t: 0.8, value: [0, 0.15, 0], ease: "sine" },
            { t: 1.6, value: [0, -0.15, 0], ease: "sine" },
            { t: 2.4, value: [0, 0.15, 0], ease: "sine" },
            { t: 3.2, value: [0, -0.15, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Fin-flutter — the signature move: the back fin spins up into a rapid
    //    flutter while the whole body does a delighted lean-forward wiggle,
    //    as if about to zoom off.
    {
      id: "fin-flutter",
      label: "Fin flutter",
      duration: 1.2,
      loop: true,
      tracks: [
        {
          target: "fin", pivot: FIN_ROOT,
          rotation: [
            { t: 0, value: [0, -0.55, 0], ease: "sine" },
            { t: 0.15, value: [0, 0.55, 0], ease: "sine" },
            { t: 0.3, value: [0, -0.55, 0], ease: "sine" },
            { t: 0.45, value: [0, 0.55, 0], ease: "sine" },
            { t: 0.6, value: [0, -0.55, 0], ease: "sine" },
            { t: 0.75, value: [0, 0.55, 0], ease: "sine" },
            { t: 0.9, value: [0, -0.55, 0], ease: "sine" },
            { t: 1.05, value: [0, 0.55, 0], ease: "sine" },
            { t: 1.2, value: [0, -0.55, 0], ease: "sine" },
          ],
        },
        {
          target: "body", pivot: COIL_TOP,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.3, value: [0.14, 0.06, 0], ease: "easeInOut" },
            { t: 0.6, value: [0.1, -0.06, 0], ease: "easeInOut" },
            { t: 0.9, value: [0.14, 0.06, 0], ease: "easeInOut" },
            { t: 1.2, value: [0, 0, 0], ease: "easeInOut" },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.6, value: [0, 0.012, 0], ease: "sine" },
            { t: 1.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
